import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { DaySummaryDto } from '@bingo/shared';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { useRefData } from '../lib/reference';
import { addDays, todayIso } from '../lib/dates';

type View = 'month' | 'week' | 'year';
interface TourCount { date: string; tourId: string | null; bookings: number; pax: number }

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const parts = (s: string) => s.split('-').map(Number) as [number, number, number];
const weekday = (s: string) => { const [y, m, d] = parts(s); return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; }; // Monday = 0
const monday = (s: string) => addDays(s, -weekday(s));
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function range(view: View, anchor: string): [string, string] {
  const [y, m] = parts(anchor);
  if (view === 'year') return [iso(y, 1, 1), iso(y, 12, 31)];
  if (view === 'week') { const s = monday(anchor); return [s, addDays(s, 6)]; }
  const first = iso(y, m, 1);
  const start = monday(first);
  return [start, addDays(start, 41)];
}

function move(view: View, anchor: string, step: number): string {
  const [y, m] = parts(anchor);
  if (view === 'year') return iso(y + step, 1, 1);
  if (view === 'week') return addDays(anchor, 7 * step);
  const nm = m + step;
  const ny = y + Math.floor((nm - 1) / 12);
  return iso(ny, ((nm - 1) % 12 + 12) % 12 + 1, 1);
}

/** Background strength grows with passengers (busy day ≈ 80 pax). */
const heat = (pax: number) => (pax ? Math.min(0.12 + pax / 100, 0.75) : 0);

/** The calendar. Embedded in Today (0.23): week by default, a day opens its Planning. */
export function Calendar({ embedded = false }: { embedded?: boolean }) {
  const [params, setParams] = useSearchParams();
  const view = (params.get('view') as View) || (embedded ? 'week' : 'month');
  const anchor = params.get('d') ?? todayIso();
  const navigate = useNavigate();
  const { byId } = useRefData();
  const [days, setDays] = useState<Map<string, DaySummaryDto>>(new Map());
  const [tours, setTours] = useState<TourCount[]>([]);
  const [from, to] = range(view, anchor);
  const today = todayIso();
  const t = useT();

  useEffect(() => {
    api<{ days: DaySummaryDto[]; tours?: TourCount[] }>(`/api/bookings/summary?from=${from}&to=${to}${view === 'week' ? '&byTour=1' : ''}`)
      .then((r) => { setDays(new Map(r.days.map((d) => [d.date, d]))); setTours(r.tours ?? []); })
      .catch(() => setDays(new Map()));
  }, [from, to, view]);

  const set = (v: View, d: string) => setParams({ view: v, d });
  const open = (d: string) => navigate(embedded ? `/planning?date=${d}` : `/bookings?date=${d}`);
  const [y, m] = parts(anchor);
  const total = useMemo(() => [...days.values()].filter((d) => d.date >= from && d.date <= to), [days, from, to]);
  const inPeriod = view === 'month' ? total.filter((d) => parts(d.date)[1] === m) : total;
  const sum = inPeriod.reduce((a, d) => ({ b: a.b + d.bookings, p: a.p + d.pax }), { b: 0, p: 0 });

  const title = view === 'year' ? String(y) : view === 'month' ? `${t(MONTHS[m - 1]!)} ${y}` : t('Week of {date}', { date: `${parts(from)[2]} ${t(MONTHS[parts(from)[1] - 1]!).slice(0, 3)} ${parts(from)[0]}` });

  return (
    <div className="stack">
      <div className={embedded ? 'cal-head' : 'page-head'}>
        <div>
          {embedded ? <h2>{t('Calendar')}</h2> : <h1>{t('Calendar')}</h1>}
          <div className="muted" style={{ fontSize: 14 }}>{t(view === 'week' ? '{b} bookings · {p} pax this week. Click a day to open it.' : view === 'month' ? '{b} bookings · {p} pax this month. Click a day to open it.' : '{b} bookings · {p} pax this year. Click a day to open it.', { b: sum.b, p: sum.p })}</div>
        </div>
        <div className="seg" role="group" aria-label={t('View')}>
          {(['week', 'month', 'year'] as View[]).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => set(v, anchor)}>{t(v[0]!.toUpperCase() + v.slice(1))}</button>
          ))}
        </div>
      </div>
      {/* Title alone on the left; the moves sit on the right, under the Week / Month / Year choice (Bruno, 19/10). */}
      <div className="cal-bar">
        <h2 className="datebar-title">{title}</h2>
        <div className="cal-moves">
          <button className="btn btn-sm" onClick={() => set(view, move(view, anchor, -1))} aria-label={t('Previous')} title={t('Previous')}>{view === 'year' ? `‹ ${y - 1}` : view === 'month' ? `‹ ${t(MONTHS[(m + 10) % 12]!)}` : `‹ ${t('Previous week')}`}</button>
          <button className="btn btn-sm" onClick={() => set(view, today)}>{t('Today')}</button>
          <button className="btn btn-sm" onClick={() => set(view, move(view, anchor, 1))} aria-label={t('Next')} title={t('Next')}>{view === 'year' ? `${y + 1} ›` : view === 'month' ? `${t(MONTHS[m % 12]!)} ›` : `${t('Next week')} ›`}</button>
        </div>
      </div>

      {view === 'month' && (
        <div className="cal">
          {DOW.map((d) => <div key={d} className="cal-dow">{t(d)}</div>)}
          {Array.from({ length: 42 }, (_, i) => addDays(from, i)).map((d) => {
            const s = days.get(d);
            const other = parts(d)[1] !== m;
            return (
              <button key={d} type="button" className={`cal-day ${other ? 'other' : ''} ${d === today ? 'today' : ''}`}
                style={{ ['--heat' as string]: heat(s?.pax ?? 0) }} onClick={() => open(d)}>
                <span className="cal-num">{parts(d)[2]}</span>
                {s && s.bookings > 0 && <span className="cal-pax"><b>{s.pax}</b> {t('pax')}</span>}
                {s && s.bookings > 0 && <span className="cal-bk">{t('{n} bk', { n: s.bookings })}</span>}
                {s && (s.missing > 0 || (d >= today && s.notConfirmed > 0)) && (
                  <span className="cal-warn" title={t('{m} missing info, {c} not CC', { m: s.missing, c: s.notConfirmed })}>{s.missing || s.notConfirmed}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {view === 'week' && (
        <div className="week">
          {Array.from({ length: 7 }, (_, i) => addDays(from, i)).map((d) => {
            const s = days.get(d);
            const list = tours.filter((x) => x.date === d).sort((a, b) => b.pax - a.pax);
            return (
              <div key={d} className={`card week-day ${d === today ? 'today' : ''}`} role="button" tabIndex={0}
                onClick={() => open(d)} onKeyDown={(e) => e.key === 'Enter' && open(d)}>
                <div className="week-head"><b>{t(DOW[weekday(d)]!)} {parts(d)[2]}</b><span className="muted">{s?.pax ?? 0} {t('pax')}</span></div>
                {list.length === 0 && <span className="muted" style={{ fontSize: 13 }}>{t('No booking')}</span>}
                {list.map((tc) => (
                  <div key={tc.tourId ?? 'none'} className="week-row">
                    <span className="mono week-tour">{tc.tourId && byId('tours', tc.tourId)?.color ? <i style={{ background: String(byId('tours', tc.tourId)!.color) }} /> : null}{tc.tourId ? String(byId('tours', tc.tourId)?.code ?? '?') : t('No tour')}</span>
                    <span className="muted" title={`${tc.bookings} ${t('bookings')} · ${tc.pax} ${t('pax')}`}>{tc.bookings} · <b>{tc.pax}</b></span>
                  </div>
                ))}
                {s && s.missing > 0 && <span className="badge badge-warn week-miss" title={t('{n} missing info', { n: s.missing })}>{s.missing} {t('missing')}</span>}
              </div>
            );
          })}
        </div>
      )}

      {view === 'year' && (
        <div className="year">
          {MONTHS.map((name, mi) => {
            const first = iso(y, mi + 1, 1);
            const lead = weekday(first);
            const n = daysIn(y, mi + 1);
            const monthPax = [...days.values()].filter((d) => parts(d.date)[0] === y && parts(d.date)[1] === mi + 1).reduce((a, d) => a + d.pax, 0);
            return (
              <div key={name} className="card year-month">
                <Link to={`${embedded ? '/' : '/calendar'}?view=month&d=${first}`} className="year-title"><b>{t(name)}</b><span className="muted">{monthPax} {t('pax')}</span></Link>
                <div className="mini">
                  {Array.from({ length: lead }, (_, i) => <span key={`l${i}`} />)}
                  {Array.from({ length: n }, (_, i) => {
                    const d = iso(y, mi + 1, i + 1);
                    const s = days.get(d);
                    return <button key={d} type="button" className={`mini-day ${d === today ? 'today' : ''}`} title={`${d}: ${s?.pax ?? 0} ${t('pax')}`}
                      style={{ ['--heat' as string]: heat(s?.pax ?? 0) }} onClick={() => open(d)}>{i + 1}</button>;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
